import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaRobot,
  FaComments,
  FaBrain,
  FaExternalLinkAlt,
  FaClock
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { aiApi, chatApi } from '../utils/apiUtils';

// AI Dashboard — one stop for every paper the user has interacted with AI on.
//
// Two tabs:
//   1. Summaries — every paper in user.aiSummarizedPapers with metadata and
//      a link back to /paper/:id (which replays the cached summary).
//   2. Chats — every ChatSession the user has opened, hydrated with the
//      paper's title/authors and a preview of the last message. Clicking
//      navigates to the paper page where the ChatPanel will open the
//      existing session automatically (server-side createOrGetSession is
//      idempotent).
//
// Designed as a navigation hub so users don't have to dig through the
// archive to find papers they've already worked with. Data is fetched on
// tab focus and kept local — no global state needed.
function AIDashboard() {
  const { user, loading: authLoading, eligibility } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summaries');

  const [summaries, setSummaries] = useState([]);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [summariesError, setSummariesError] = useState('');

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');

  // Bounce unauthenticated users. We let AuthContext finish loading first so
  // we don't redirect mid-refresh after a navigation.
  useEffect(() => {
    if (!authLoading && !user) navigate('/sign-in');
  }, [authLoading, user, navigate]);

  const loadSummaries = useCallback(async () => {
    setSummariesLoading(true);
    setSummariesError('');
    try {
      const { summaries } = await aiApi.listSummaries();
      setSummaries(summaries || []);
    } catch (err) {
      setSummariesError(err.response?.data?.message || 'Failed to load summaries');
    } finally {
      setSummariesLoading(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError('');
    try {
      const { sessions } = await chatApi.listSessions();
      setSessions(sessions || []);
    } catch (err) {
      setSessionsError(err.response?.data?.message || 'Failed to load chat sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadSummaries();
    loadSessions();
  }, [user, loadSummaries, loadSessions]);

  if (authLoading) {
    return (
      <div className="max-w-5xl mx-auto py-16 text-center">
        <div className="w-6 h-6 border-2 border-white/20 border-t-yellow-300 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/30 text-xs">Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  const formatQuota = (usage, quota) => {
    if (quota === 0) return 'Not included';
    if (!Number.isFinite(quota)) return `${usage} / Unlimited`;
    return `${usage} / ${quota}`;
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '';
    const diffMs = Date.now() - then.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return then.toLocaleDateString();
  };

  const tabs = [
    {
      id: 'summaries',
      label: 'Summaries',
      icon: FaRobot,
      count: summaries.length,
      quota: formatQuota(eligibility.aiSummaryUsage, eligibility.aiSummaryQuota)
    },
    {
      id: 'chats',
      label: 'Chats',
      icon: FaComments,
      count: sessions.length,
      quota: formatQuota(eligibility.aiChatUsage, eligibility.aiChatQuota)
    }
  ];

  const renderEmpty = (type) => (
    <div className="bg-gray-50 rounded-xl p-12 text-center">
      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mx-auto mb-3">
        {type === 'summaries' ? (
          <FaRobot className="text-purple-400 text-sm" />
        ) : (
          <FaComments className="text-purple-400 text-sm" />
        )}
      </div>
      <p className="text-xs text-gray-500 font-semibold mb-1">
        {type === 'summaries'
          ? 'No AI summaries yet'
          : 'No chat sessions yet'}
      </p>
      <p className="text-[11px] text-gray-400 mb-4">
        {type === 'summaries'
          ? 'Open any paper and generate a summary to see it here.'
          : 'Open a paper and start a chat to see sessions here.'}
      </p>
      <Link
        to="/"
        className="inline-flex items-center px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors"
      >
        Browse papers
      </Link>
    </div>
  );

  const renderLoading = () => (
    <div className="py-12 text-center">
      <div className="w-5 h-5 border-2 border-gray-200 border-t-purple-500 rounded-full animate-spin mx-auto mb-3" />
      <p className="text-[11px] text-gray-400">Loading...</p>
    </div>
  );

  const renderError = (message, retry) => (
    <div className="text-[11px] text-red-500 bg-red-50 rounded px-3 py-2 border border-red-100 flex items-center justify-between">
      <span>{message}</span>
      <button
        onClick={retry}
        className="ml-3 text-red-600 hover:text-red-800 font-semibold underline"
      >
        Retry
      </button>
    </div>
  );

  const renderSummaries = () => {
    if (summariesLoading) return renderLoading();
    if (summariesError) return renderError(summariesError, loadSummaries);
    if (summaries.length === 0) return renderEmpty('summaries');

    return (
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {summaries.map((s) => (
          <Link
            key={s.id}
            to={`/ai/summary/${s.id}`}
            className="group block p-4 rounded-xl border border-gray-100 bg-white hover:border-purple-200 hover:shadow-card transition-all"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <FaBrain className="text-purple-400 text-[10px] flex-shrink-0" />
                <span className="text-[9px] font-bold uppercase tracking-wider text-purple-500 truncate">
                  {s.topic}
                </span>
              </div>
              <FaExternalLinkAlt className="text-gray-200 group-hover:text-purple-400 text-[9px] transition-colors flex-shrink-0" />
            </div>
            <h3 className="text-xs font-bold text-gray-900 leading-snug mb-1.5 line-clamp-2">
              {s.title}
            </h3>
            <p className="text-[11px] text-gray-500 truncate mb-3">
              {s.first_author}
            </p>
            <div className="flex items-center justify-between text-[10px] text-gray-400">
              <span className="flex items-center gap-1">
                <FaClock className="text-[9px]" />
                {formatRelative(s.summary_generated_at)}
              </span>
              {s.summary_model && (
                <span className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-[9px]">
                  {s.summary_model}
                </span>
              )}
            </div>
          </Link>
        ))}
      </motion.div>
    );
  };

  const renderChats = () => {
    if (sessionsLoading) return renderLoading();
    if (sessionsError) return renderError(sessionsError, loadSessions);
    if (sessions.length === 0) return renderEmpty('chats');

    return (
      <motion.div
        className="space-y-2"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {sessions.map((s) => {
          const paperTitle = s.paper?.title || s.title || 'Untitled paper';
          const author = s.paper?.first_author;
          const preview = s.lastMessagePreview || 'No messages yet.';
          const roleLabel =
            s.lastMessageRole === 'assistant'
              ? 'AI'
              : s.lastMessageRole === 'user'
              ? 'You'
              : '';
          return (
            <Link
              key={s.id}
              to={`/ai/chats/${s.id}`}
              className="group block p-4 rounded-xl border border-gray-100 bg-white hover:border-purple-200 hover:shadow-card transition-all"
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-1">
                    <FaComments className="text-purple-400 text-[10px] flex-shrink-0" />
                    {s.paper?.topic && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-purple-500 truncate">
                        {s.paper.topic}
                      </span>
                    )}
                    <span className="text-gray-200 text-[10px]">•</span>
                    <span className="text-[10px] text-gray-400 tabular-nums">
                      {s.messageCount} {s.messageCount === 1 ? 'message' : 'messages'}
                    </span>
                  </div>
                  <h3 className="text-xs font-bold text-gray-900 leading-snug line-clamp-1">
                    {paperTitle}
                  </h3>
                  {author && (
                    <p className="text-[11px] text-gray-500 truncate">{author}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] text-gray-400 flex items-center gap-1">
                    <FaClock className="text-[9px]" />
                    {formatRelative(s.lastMessageAt || s.updatedAt)}
                  </span>
                  <FaExternalLinkAlt className="text-gray-200 group-hover:text-purple-400 text-[9px] transition-colors" />
                </div>
              </div>
              <div className="mt-2 pl-4 border-l-2 border-purple-100">
                <p className="text-[11px] text-gray-600 line-clamp-2">
                  {roleLabel && (
                    <span className="font-bold text-purple-500 mr-1.5">
                      {roleLabel}:
                    </span>
                  )}
                  {preview}
                </p>
              </div>
            </Link>
          );
        })}
      </motion.div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto py-4 sm:py-8">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-glass overflow-hidden border border-gray-100 mb-5">
        <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400" />
        <div className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
            <FaBrain className="text-purple-400 text-sm" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">AI Digest</h1>
            <p className="text-xs text-gray-400 truncate">
              Your AI-generated summaries and paper chats in one place.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs + content */}
      <div className="bg-white rounded-2xl shadow-glass overflow-hidden border border-gray-100">
        <div className="border-b border-gray-100">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold transition-all duration-200 border-b-2 ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-300 hover:text-gray-500'
                }`}
              >
                <tab.icon
                  className={`text-[10px] ${
                    activeTab === tab.id ? 'text-purple-400' : 'text-gray-200'
                  }`}
                />
                {tab.label}
                <span
                  className={`px-1.5 py-0.5 rounded text-[9px] font-bold tabular-nums ${
                    activeTab === tab.id
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-50 text-gray-400'
                  }`}
                >
                  {tab.count}
                </span>
                <span className="hidden sm:inline text-[9px] text-gray-300 font-normal tabular-nums">
                  · {tab.quota}
                </span>
              </button>
            ))}
          </nav>
        </div>
        <div className="p-5">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'summaries' ? renderSummaries() : renderChats()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default AIDashboard;
